import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { setAnalyticsState } from '../analytics';
import { AffiliateDetails, BrandContent, CurrentBrand, RecordSelectorType } from "modules/Types";
import { PlanType, SortDirection, UserPermissionOnBrand } from "modules/Const";
import { BrandUsersApiResponse, CreateBrandPayload, createUserBrand_api, deleteUserFromBrand_api, createBrand_api, getBrandUsers_api, getBrand_api, getBrands_api, getReleasesByBrandId_api, BrandUser } from 'api/brandApi';
import * as Sentry from '@sentry/browser';
import Logger from 'Logger';
import { arrayToObject, deepCopy, sortIds } from 'modules/Utils';
import { updateAxiosDefaults } from 'helpers/Interceptor';
import { UnauthenticatedAxios } from 'helpers/Interceptor';
import { createTrialForBrand } from 'api/adminApi';
import useFacebookConnections from './useFacebookConnections';
import ProfileIcon from "assets/images/profileIcon.svg";
import useTikTokConnections from './useTikTokConnections';
import { getRecordDetails_api, refreshBrandContent_api } from 'api/brandContentApi';
import { useCampaign } from './useCampaign';
import { getPartnerDiscount } from 'helpers/partners';
import useUser from './useUser';
import useFans from './useFans';
import useOrganizationV2 from './useOrganizationV2';
import useMaestroTasks from './useMaestroTasks';
import useMessageLimits from './useMessageLimits';
import useMaestroChat from './useMaestroChat';
import useWebsites from './useWebsites';
import useWebsiteBuilder from './useWebsiteBuilder';

export const checkIsPaidTier = (currentBrand?: CurrentBrand | null): boolean => {
    return currentBrand?.tier !== PlanType.FREE;
};

export const checkIsOnNewSubscriptionPlan = (currentBrand?: CurrentBrand | null): boolean => {
    return currentBrand?.tier === PlanType.STARTER ||
        currentBrand?.tier === PlanType.GROWTH ||
        currentBrand?.tier === PlanType.TEAM_V2;
}

export const checkIsTeamTier = (currentBrand?: CurrentBrand | null): boolean => {
    return currentBrand?.tier === PlanType.TEAM;
};

export const checkIsProTier = (currentBrand?: CurrentBrand | null): boolean => {
    return currentBrand?.tier === PlanType.PRO ||
        currentBrand?.tier === PlanType.TEAM ||
        currentBrand?.tier === PlanType.GROWTH ||
        currentBrand?.tier === PlanType.TEAM_V2;
};

export const checkIsLiteTier = (currentBrand?: CurrentBrand | null): boolean => {
    return currentBrand?.tier === PlanType.LITE;
};

export const checkIsFreeTier = (currentBrand?: CurrentBrand | null): boolean => {
    return currentBrand?.tier === PlanType.FREE;
};

export const checkIsStarterTier = (currentBrand?: CurrentBrand | null): boolean => {
    return currentBrand?.tier === PlanType.STARTER;
}

export const checkIsGrowthTier = (currentBrand?: CurrentBrand | null): boolean => {
    return currentBrand?.tier === PlanType.GROWTH;
}

export const checkIsTeamV2Tier = (currentBrand?: CurrentBrand | null): boolean => {
    return currentBrand?.tier === PlanType.TEAM_V2;
}

// Helper function to determine if a brand is grandfathered
export const checkIsGrandfathered = (currentBrand?: CurrentBrand | null): boolean => {
    if (!currentBrand) {
        return false; // Anonymous or uninitialized brands are not grandfathered
    }

    // Primary source of truth: the isGrandfathered flag from the backend (via brand.customization)
    // User's note: currentBrand.customization.isGrandfathered will be the eventual source.
    // For now, we check currentBrand.isGrandfathered which will be populated from customization if it exists.
    if (typeof currentBrand.isGrandfathered === 'boolean') {
        return currentBrand.isGrandfathered;
    }

    // Fallback logic: if the flag isn't directly on CurrentBrand (e.g. older cached data before type update)
    // or not yet provided by backend on currentBrand.customization.
    // Check based on old plan types.
    // This also implicitly handles `brand.subscription.onOldPlan` if that logic is embedded in how `tier` is set for old plans.
    const oldTiers: PlanType[] = [PlanType.PRO, PlanType.LITE, PlanType.TEAM];
    if (currentBrand.tier && oldTiers.includes(currentBrand.tier as PlanType)) {
        return true;
    }

    return false;
};

export const isBrandAdmin = (currentBrand: CurrentBrand | undefined) => {
    return currentBrand?.permissionLevel === UserPermissionOnBrand.ADMINISTRATOR || currentBrand?.permissionLevel === UserPermissionOnBrand.OWNER
}

export const checkTier = (currentBrand?: CurrentBrand | null): PlanType => {
    if (!currentBrand) {
        return PlanType.FREE;
    }

    switch (currentBrand.tier) {
        case PlanType.PRO:
            return PlanType.PRO;
        case PlanType.LITE:
            return PlanType.LITE;
        case PlanType.TEAM:
            return PlanType.TEAM;
        case PlanType.STARTER:
            return PlanType.STARTER;
        case PlanType.GROWTH:
            return PlanType.GROWTH;
        case PlanType.TEAM_V2:
            return PlanType.TEAM_V2;
        case PlanType.FREE:
        default:
            return PlanType.FREE;
    }
}

const saveBrandToLocalStorage = (brandId: number) => {
    localStorage.setItem('selected-brand', String(brandId));
}

const getBrandFromLocalStorage = () => {
    return localStorage.getItem('selected-brand');
}

export const removeBrandFromLocalStorage = () => {
    localStorage.removeItem('selected-brand');
}

export const saveAnonymousSessionIdToLocalStorage = (sessionId: string) => {
    localStorage.setItem('anonymous-session-id', sessionId);
}

export const getAnonymousSessionIdFromLocalStorage = () => {
    return localStorage.getItem('anonymous-session-id');
}

export const removeAnonymousSessionIdFromLocalStorage = () => {
    localStorage.removeItem('anonymous-session-id');
}

export interface BrandState {
    /* States */
    isFirstSession: boolean;
    anonymousBrandEmail: string;
    currentBrand: CurrentBrand | undefined;
    loadedBrands: CurrentBrand[] | undefined;
    loadedBrandsDict: { [key: string]: CurrentBrand } | undefined;
    isLoadingBrand: boolean;
    isLoadingBrands: boolean;
    isPaidBrand: boolean;
    isTeamBrand: boolean;
    isProBrand: boolean;
    isLiteBrand: boolean;
    isFreeBrand: boolean;
    isStarterBrand: boolean;
    isGrowthBrand: boolean;
    isTeamV2Brand: boolean;
    isBrandAdmin: boolean;
    isGrandfathered: boolean; // Added for grandfathering status
    isOnNewSubscriptionPlan: boolean;
    isPreviewingTier: boolean;

    /** Anonymous/Temporary Brand State */
    isAnonymousBrand: boolean;
    temporarySessionId: string | null;
    isInitializingTemporaryBrand: boolean;

    /** Release-related state */
    recentReleases: BrandContent[] | null;
    isLoadingReleases: boolean;
    hasReleasesError: boolean;
    isRefreshingReleases: boolean;

    /** Websites-related state */
    websites: BrandContent[] | null;
    isLoadingWebsites: boolean;
    hasWebsitesError: boolean;
    isRefreshingWebsites: boolean;
    isWebsitesInitialized: boolean;

    /* Actions */
    setAnonymousBrandEmail: (email: string) => void;
    setIsFirstSession: (isFirstSession: boolean) => void;
    setCurrentBrand: (brand: CurrentBrand | undefined, ignoreOrganization?: boolean) => void;
    resetBrandData: (preserveOrganization?: boolean) => void;
    setLoadedBrandsDict: (brands: { [key: string]: CurrentBrand }) => void;
    setLoadedBrands: (brands: CurrentBrand[]) => void;
    setWebsites: (websites: BrandContent[]) => void;
    updateOrAddBrand: (brand: CurrentBrand) => void;
    updateOrAddBrands: (newBrands: CurrentBrand[]) => void;
    getBrandImage: (brandOverride?: CurrentBrand) => string;
    getFanbaseSize: (brandOverride?: CurrentBrand) => number;

    /* Methods */
    createBrand: (brand: CreateBrandPayload) => Promise<CurrentBrand>;
    getBrand: (brandId?: number) => Promise<CurrentBrand>;
    getBrands: (isSetCurrentBrand?: boolean, ignoreOrganization?: boolean) => Promise<CurrentBrand[] | CurrentBrand | null>;
    sortBrands: (brandIds: number[], key: string, direction: SortDirection) => number[];
    removeBrand: (brandId: number) => void;
    sortBrandsArray: (brands: CurrentBrand[], key?: string, direction?: SortDirection) => CurrentBrand[];
    updateBrandTierPreviewLocally: (tier: PlanType | null) => void;
    admin_createTrialSubscription: (brandId: number, params: {
        planType: PlanType;
        trialDays?: number;
        userEmail: string;
    }) => Promise<CurrentBrand>;
    getIsPaymentFailed: () => boolean;
    getIsPaymentPastDue: () => boolean;
    getIsPaymentPaused: () => boolean;
    getAffiliatePartnerDetails: () => AffiliateDetails | null;
    getPartnerDiscountText: (subscriptionType: PlanType, discountAmount: number) => string | null;

    /* Methods for releases */
    fetchReleases: (brand?: CurrentBrand) => Promise<void>;
    refreshReleases: () => Promise<void>;

    getBrandUsers: () => Promise<BrandUsersApiResponse>;
    addUserBrand: (email: string) => Promise<BrandUser | null>;
    deleteUserFromBrand: (brandUser: BrandUser) => Promise<BrandUsersApiResponse>;

    /** Anonymous/Temporary Brand Methods */
    ensureBrandAvailable: () => Promise<CurrentBrand>;
    clearTemporaryBrand: () => void;
    /** Get or create anonymous session ID from localStorage */
    getOrCreateAnonymousSessionId: () => string;
    /** Selector to determine if a new user should see an upgrade prompt */
    getShouldShowNewUserUpgradePrompt: () => boolean;
    /** Check if user has any view-only team brands */
    hasViewOnlyTeamBrands: () => boolean;
}

export interface BrandExistsState extends BrandState {
    currentBrand: CurrentBrand;
    loadedBrands: CurrentBrand[];
    loadedBrandsDict: { [key: string]: CurrentBrand };
}

const filterValidReleases = (content: BrandContent[]) => {
    return content.filter(({ content_metadata }) => {
        if (content_metadata.type === 'project') return true
        if (
            content_metadata.type === 'record' &&
            content_metadata?.presave?.saved_at &&
            content_metadata?.spotify_id
        ) return true
        if (
            content_metadata.type === 'playlist' &&
            content_metadata?.published === true
        ) return true
        return false
    })
}

const useBrand = create(immer<BrandState>((set, get) => ({
    currentBrand: undefined,
    loadedBrands: undefined,
    loadedBrandsDict: undefined,
    isLoadingBrand: false,
    isLoadingBrands: false,
    isPaidBrand: false,
    isTeamBrand: false,
    isProBrand: false,
    isLiteBrand: false,
    isFreeBrand: false,
    isStarterBrand: false,
    isGrowthBrand: false,
    isTeamV2Brand: false,
    isBrandAdmin: false,
    isGrandfathered: false, // Initialized to false
    isOnNewSubscriptionPlan: false,
    isPreviewingTier: false,
    isFirstSession: false,

    /** Anonymous/Temporary Brand State */
    isAnonymousBrand: false,
    anonymousBrandEmail: '',
    setAnonymousBrandEmail: (email: string) => {
        set(state => {
            state.anonymousBrandEmail = email;
        });
    },
    temporarySessionId: null,
    isInitializingTemporaryBrand: false,

    /** Release-related state */
    recentReleases: null,
    isLoadingReleases: false,
    hasReleasesError: false,
    isRefreshingReleases: false,

    /** Websites-related state */
    websites: [],
    isLoadingWebsites: false,
    hasWebsitesError: false,
    isRefreshingWebsites: false,
    isWebsitesInitialized: false,

    /** View-only team brand restrictions */
    hasViewOnlyTeamBrands: () => {
        const { loadedBrands } = get();
        return loadedBrands?.some(brand => 
            brand.permissionLevel === UserPermissionOnBrand.VIEW_ONLY && 
            brand.organizationId
        ) || false;
    },

    setIsFirstSession: (isFirstSession: boolean) => {
        set(state => {
            state.isFirstSession = isFirstSession;
        });
    },

    setCurrentBrand: (brand: CurrentBrand | undefined, ignoreOrganization?: boolean) => {
        if (!brand) {
            // Clear current brand (for team home view)
            updateAxiosDefaults.clearBrandId();
            console.log('🔄 Clearing current brand');
            set(state => {
                state.currentBrand = undefined;
                localStorage.removeItem("selected-brand");
            });

            return;
        }

        const id = brand.id || Number(localStorage.getItem("selected-brand"));
        updateAxiosDefaults.setBrandId(id);
        console.log('🔄 Setting current brand:', id);
        const {
            fetchJobs,
            fetchChats
        } = useMaestroChat.getState();
        const {
            currentBrand,
            fetchReleases,
            getBrandUsers,
            resetBrandData
        } = get();
        const {
            getLegacyWebsites,
            getWebsiteBuilderWebsites
        } = useWebsites.getState();
        const {
            currentUser
        } = useUser.getState();
        const {
            fetchMessageUsage
        } = useMessageLimits.getState();
        const { getCampaigns } = useCampaign.getState();
        const { initializeTikTok } = useTikTokConnections.getState();
        const {
            getOrganization,
            organization
        } = useOrganizationV2.getState();
        const {
            initializeFB
        } = useFacebookConnections.getState();
        const {
            refreshTaskData,
        } = useMaestroTasks.getState();

        const isDifferentBrand = id !== currentBrand?.id;

        set(state => {
            saveBrandToLocalStorage(brand.id);
            state.loadedBrandsDict = { ...state.loadedBrandsDict, [brand.id]: brand }; // NOTE: might not need
            state.loadedBrands = state.loadedBrands?.map(b => b.id === brand.id ? brand : b) || [brand]; // NOTE: might not need
            state.currentBrand = brand;
            state.isPreviewingTier = false;
            state.isPaidBrand = checkIsPaidTier(brand);
            state.isOnNewSubscriptionPlan = checkIsOnNewSubscriptionPlan(brand);
            state.isTeamBrand = checkIsTeamTier(brand);
            state.isProBrand = checkIsProTier(brand);
            state.isLiteBrand = checkIsLiteTier(brand);
            state.isFreeBrand = checkIsFreeTier(brand);
            state.isStarterBrand = checkIsStarterTier(brand);
            state.isGrowthBrand = checkIsGrowthTier(brand);
            state.isTeamV2Brand = checkIsTeamV2Tier(brand);
            state.isBrandAdmin = isBrandAdmin(brand);
            state.isGrandfathered = checkIsGrandfathered(brand); // Set grandfathered status

            // Reset recent releases when setting a new brand

            if (isDifferentBrand) {
                resetBrandData() // Clear organization data since new brand might have different org
            }
        });

        // Set analytics context with brand data
        setAnalyticsState({
            id: brand.id,
            name: brand.name,
            slug: brand.slug,
            tier: brand.tier,
            subscription: brand.subscription
        });

        // get message usage
        fetchMessageUsage(brand.id);

        // useTikTokConnections
        initializeTikTok(brand);

        // Facebook Connection Handling
        // initializeFB now handles clearing old state and setting up new brand atomically
        if (isDifferentBrand) {
            initializeFB(brand); // Clear old state and initialize with new brand
        }


        fetchReleases(brand);

        // useCampaigns
        const brandId = brand.id;
        getCampaigns({ brandId })

        // useWebsites (consolidated website fetching)
        getLegacyWebsites({ brandId });
        getWebsiteBuilderWebsites({ brandId });

        // useOrganizationV2
        if (!ignoreOrganization) {
            getOrganization({
                brandId: brand.id,
                userId: currentUser?.id,
            });
        }

        refreshTaskData(brand.id);
        fetchJobs()
        fetchChats()
    },

    resetBrandData: (preserveOrganization: boolean = false) => {
        const {
            setIsFirstLoad,
            setFansData,
            setFansCount
        } = useFans.getState();
        const {
            setWebsiteBuilderSites: setWebsiteBuilderWebsites,
        } = useWebsites.getState();
        const {
            resetWebsiteBuilderLocalStorage
        } = useWebsiteBuilder.getState();

        const {
            setCampaigns,
            setCampaignDrafts,
            setIsCampaignsInitialized,
            resetCampaignFilter
        } = useCampaign.getState();

        const {
            setOrganization
        } = useOrganizationV2.getState();

        const {
            resetTaskData
        } = useMaestroTasks.getState();
        
        console.log(`HOOK: resetBrandData (preserveOrganization: ${preserveOrganization})`)
        set(state => {
            // useBrand
            state.isWebsitesInitialized = false;
            state.websites = [];
            state.recentReleases = null;
            state.hasReleasesError = false;
            state.recentReleases = null;

            // useFans
            setIsFirstLoad(true);
            setFansData([]);
            setFansCount(0);

            // useWebsites
            setWebsiteBuilderWebsites([])

            // useWebsiteBuilder
            resetWebsiteBuilderLocalStorage()

            // useMarketingCampaigns
            setIsCampaignsInitialized(false)
            setCampaigns([])
            setCampaignDrafts([])
            resetCampaignFilter(0)

            // Conditionally clear organization data
            if (!preserveOrganization) {
                setOrganization(undefined);
            }

            // useMaestroTasks
            resetTaskData();
        });
    },



    /** Updates the loaded brands array in state */
    setLoadedBrands: (brands: CurrentBrand[]) => { set(state => { state.loadedBrands = brands }); },

    setWebsites: (websites: BrandContent[]) => { set(state => { state.websites = websites }); },

    /** Updates the loaded brands dictionary in state */
    setLoadedBrandsDict: (brands: { [key: string]: CurrentBrand }) => { set(state => { state.loadedBrandsDict = brands }); },
    /** Updates an existing brand or adds a new one to both dictionary and array */
    updateOrAddBrand: (newBrand: CurrentBrand) => {
        const { loadedBrandsDict, loadedBrands } = get();

        set(state => {
            const updatedBrandsDict = { ...loadedBrandsDict, [newBrand.id]: newBrand };
            state.loadedBrandsDict = updatedBrandsDict;
            let updatedBrands = !loadedBrands ? [newBrand] : [...loadedBrands.filter(b => b.id !== newBrand.id), newBrand]
            state.loadedBrands = updatedBrands;
        });
    },

    /** 
     * Gets the affiliate partner details for the current brand if they exist.
     * First checks if the brand has a partnerId in its affiliate_details and looks up corresponding discount.
     * If no brand partner details found, falls back to checking the current user's metadata for affiliate details.
     * Used to determine discounts and special pricing for brands associated with partners.
     * @returns {AffiliateDetails | null} Partner details object containing discount info and other partner metadata if found, null if no partner association exists
     */
    getAffiliatePartnerDetails: (): AffiliateDetails | null => {
        const { currentBrand } = get();

        const {
            currentUser
        } = useUser.getState();

        let affiliateDetails = currentBrand?.affiliate_details || currentUser?.metadata?.affiliate_details;
        if (affiliateDetails?.partnerId) {
            let partnerDetails = getPartnerDiscount[affiliateDetails.partnerId];
            if (partnerDetails) {
                return partnerDetails;
            }
        }

        return null;
    },

    /**
     * Gets the partner discount text for display in banners/UI
     * Centralizes the discount percentage calculation to ensure consistency
     * @param {PlanType} subscriptionType - The plan type (PRO or LITE)
     * @param {number} discountAmount - The discount multiplier (0.75 = pay 75% of price = 25% off)
     * @returns {string | null} Formatted discount text or null if no discount
     */
    getPartnerDiscountText: (subscriptionType: PlanType, discountAmount: number): string | null => {
        const partnerDetails = get().getAffiliatePartnerDetails();
        const partnerId = partnerDetails?.partnerId;
        const partnerName = partnerDetails?.partnerName;

        if (!discountAmount || !partnerId) {
            return null;
        }

        // The discountAmount is a price multiplier (0.75 = pay 75% of price = 25% discount)
        const discountPercentage = (1 - discountAmount) * 100;
        const basePartnerLabel = `${discountPercentage}% off ${subscriptionType.toUpperCase()} for 12 months!`;
        return `${partnerName} users receive ${basePartnerLabel}`;
    },

    getBrandImage: (brandOverride?: CurrentBrand) => {
        const { currentBrand } = get();
        const currentBrandLocal = brandOverride || currentBrand;
        const { connections } = currentBrandLocal || {};
        if (currentBrandLocal?.image) {
            return currentBrandLocal.image;
        }
        if (!connections) {
            return '';
        }

        const {
            spotify_artist_page,
            tiktok,
            business_discovery_instagram,
            instagram_page,
            youtube_channel,
            facebook_page,
        } = connections;

        if (spotify_artist_page?.thumbnail_url) {
            return spotify_artist_page.thumbnail_url || '';
        }
        if (spotify_artist_page?.images?.length > 0) {
            return spotify_artist_page.images[0].url || '';
        }

        if (business_discovery_instagram?.profile_picture_url) {
            return business_discovery_instagram.profile_picture_url || '';
        }
        if (instagram_page?.instagramAccount?.profile_picture_url) {
            return instagram_page.instagramAccount.profile_picture_url || ''
        }

        if (tiktok?.picture) {
            return tiktok?.picture || '';
        }

        if (youtube_channel?.snippet?.thumbnails?.high?.url) {
            return youtube_channel.snippet.thumbnails.high.url || ''
        }

        if (facebook_page?.picture?.data?.url) {
            return facebook_page.picture.data.url || ''
        }

        return ProfileIcon;
    },

    getFanbaseSize: (brandOverride?: CurrentBrand): number => {
        const { currentBrand } = get();
        const currentBrandLocal = brandOverride || currentBrand;

        let fanbaseSize = currentBrandLocal?.fanbaseSize || 0;

        if (fanbaseSize > 0) {
            return fanbaseSize
        }

        const { connections } = currentBrandLocal || {};

        if (!connections) {
            return fanbaseSize;
        }

        const {
            spotify_artist_page,
            business_discovery_instagram,
            instagram_page,
        } = connections || {};


        if (spotify_artist_page?.followers?.total) {
            fanbaseSize += spotify_artist_page.followers.total;
        } else if (spotify_artist_page?.followers && !isNaN(Number(spotify_artist_page.followers))) {
            fanbaseSize += Number(spotify_artist_page.followers);
        }

        if (business_discovery_instagram?.followers_count) {
            fanbaseSize += Number(business_discovery_instagram.followers_count);
        }

        if (instagram_page?.instagramAccount?.follow_count) {
            fanbaseSize += Number(instagram_page.instagramAccount.follow_count);
        }

        return fanbaseSize || 0;
    },

    /** Updates multiple existing brands or adds new ones to both dictionary and array */
    updateOrAddBrands: (newBrands: CurrentBrand[]) => {
        const { loadedBrandsDict, loadedBrands } = get();

        set(state => {
            const updatedBrandsDict = { ...loadedBrandsDict };
            newBrands.forEach(brand => {
                updatedBrandsDict[brand.id] = brand;
            });
            state.loadedBrandsDict = updatedBrandsDict;

            let updatedBrands = !loadedBrands ?
                [...newBrands] :
                [...loadedBrands.filter(b => !newBrands.some(nb => nb.id === b.id)), ...newBrands];

            state.loadedBrands = updatedBrands;
        });
    },

    /** Creates a new brand via API and updates state */
    createBrand: async (brand: CreateBrandPayload) => {
        const { updateOrAddBrand } = get();
        try {
            set({ isLoadingBrand: true });
            const newBrand = await createBrand_api(brand);

            updateOrAddBrand(newBrand);
            return newBrand;
        } catch (error) {
            throw error;
        } finally {
            set({ isLoadingBrand: false });
        }
    },

    /** Fetches a single brand by ID and updates state */
    getBrand: async (brandId?: number) => {
        const { currentBrand } = get();

        set({ isLoadingBrand: true });
        try {
            const reloadedBrand = await getBrand_api(brandId || currentBrand?.id || 0);
            if (!reloadedBrand) {
                throw new Error('useBrand::getBrand::Failed to reload brand');
            }

            set({
                currentBrand: reloadedBrand,
                isPaidBrand: checkIsPaidTier(reloadedBrand),
                isOnNewSubscriptionPlan: checkIsOnNewSubscriptionPlan(reloadedBrand),
                isTeamBrand: checkIsTeamTier(reloadedBrand),
                isProBrand: checkIsProTier(reloadedBrand),
                isLiteBrand: checkIsLiteTier(reloadedBrand),
                isFreeBrand: checkIsFreeTier(reloadedBrand),
                isStarterBrand: checkIsStarterTier(reloadedBrand),
                isGrowthBrand: checkIsGrowthTier(reloadedBrand),
                isTeamV2Brand: checkIsTeamV2Tier(reloadedBrand),
                isBrandAdmin: isBrandAdmin(reloadedBrand),
                isGrandfathered: checkIsGrandfathered(reloadedBrand),
                isLoadingBrand: false,
            });

            return reloadedBrand;
        } catch (error) {
            set({ isLoadingBrand: false });
            throw error;
        }
    },

    /** Fetches all brands and optionally sets current brand */
    getBrands: async (isSetCurrentBrand?: boolean, ignoreOrganization?: boolean): Promise<CurrentBrand[] | CurrentBrand | null> => {
        const { setCurrentBrand } = get();

        try {
            set({ isLoadingBrands: true });

            const brandsArray = await getBrands_api();
            let initialBrand = null;
            if (isSetCurrentBrand && brandsArray.length > 0) {
                initialBrand = brandsArray[0]
                const alreadySelectedBrandId = getBrandFromLocalStorage();
                if (alreadySelectedBrandId) {
                    // NOTE: need to find in array because we have not set the loadedBrandsDict yet
                    initialBrand = brandsArray.find((o) => o.id === parseInt(alreadySelectedBrandId)) || brandsArray[0];
                }

                saveBrandToLocalStorage(initialBrand.id);
                setCurrentBrand(initialBrand, ignoreOrganization);
            }

            set(state => {
                state.loadedBrands = brandsArray
                state.loadedBrandsDict = arrayToObject(brandsArray) || {}
            });

            if (isSetCurrentBrand) {
                return initialBrand;
            }

            return brandsArray.length > 0 ? brandsArray : [];
        } catch (err) {
            set({ loadedBrands: [] });

            if ((err as any).response?.status === 500) {
                Sentry.captureException(`Error getting the brands with status 500: ${err}`);
            }
            Logger.error('Error fetching brands:', err);
            return null;
        } finally {
            set({ isLoadingBrands: false });
        }
    },

    /** Removes a brand from local state */
    removeBrand: (brandId: number) => {
        const { loadedBrandsDict, loadedBrands } = get();
        delete loadedBrandsDict?.[brandId];
        const brandsLocal = (loadedBrands || []).filter(brand => brand.id !== brandId);
        set({
            loadedBrands: brandsLocal,
            loadedBrandsDict
        });
    },

    /** Sorts brand IDs based on a key and direction */
    sortBrands: (brandIds: number[], key: string, direction: SortDirection = SortDirection.DESC) => {
        const { loadedBrandsDict } = get();
        return sortIds(loadedBrandsDict, brandIds, key, direction);
    },

    /** Sorts an array of brands based on a key and direction */
    sortBrandsArray: (brands: CurrentBrand[], key: string = 'name', direction: SortDirection = SortDirection.DESC) => {
        const { loadedBrands } = get();
        let loadedBrandsCopy = brands || deepCopy(loadedBrands);
        if (!loadedBrandsCopy) {
            return [];
        }

        const sortedBrands = loadedBrandsCopy.sort((a, b) => {
            const aValue = a[key as keyof CurrentBrand];
            const bValue = b[key as keyof CurrentBrand];
            return direction === SortDirection.DESC ?
                String(bValue).localeCompare(String(aValue)) :
                String(aValue).localeCompare(String(bValue));
        });

        return sortedBrands;
    },

    /** 
     * Updates the brand's subscription tier locally in the store without making an API call
     * Updates the current brand, loaded brands dictionary, loaded brands array, and all tier status flags
     * @param tier - The new PlanType subscription tier to set for the brand
     */
    updateBrandTierPreviewLocally: (tier: PlanType | null) => {
        const { currentBrand } = get();
        if (!currentBrand) return;

        const updatedBrand = {
            ...currentBrand,
        };

        let isPreviewingTier = false;

        if (tier && tier !== PlanType.FREE) {
            updatedBrand.tier = tier;
            isPreviewingTier = true;
        } else {
            updatedBrand.tier = PlanType.FREE;
        }

        set(state => {
            state.currentBrand = updatedBrand;
            state.loadedBrandsDict = {
                ...state.loadedBrandsDict,
                [currentBrand.id]: updatedBrand
            };
            state.loadedBrands = state.loadedBrands?.map(b =>
                b.id === currentBrand.id ? updatedBrand : b
            );
            state.isPreviewingTier = isPreviewingTier;
            state.isPaidBrand = checkIsPaidTier(updatedBrand);
            state.isProBrand = checkIsProTier(updatedBrand);
            state.isLiteBrand = checkIsLiteTier(updatedBrand);
            state.isFreeBrand = checkIsFreeTier(updatedBrand);
            state.isStarterBrand = checkIsStarterTier(updatedBrand);
            state.isGrowthBrand = checkIsGrowthTier(updatedBrand);
            state.isTeamV2Brand = checkIsTeamV2Tier(updatedBrand);
        });
    },

    /** Creates a trial subscription for a brand and updates local state */
    admin_createTrialSubscription: async (brandId: number, params: {
        planType: PlanType;
        trialDays?: number;
        userEmail: string;
    }) => {
        const {
            updateOrAddBrand,
            setCurrentBrand
        } = get();
        try {
            const updatedBrand = await createTrialForBrand({
                ...params,
                brandId
            });
            updateOrAddBrand(updatedBrand);
            setCurrentBrand(updatedBrand);
            return updatedBrand;
        } catch (error) {
            throw error;
        }
    },

    getIsPaymentFailed: () => {
        const { currentBrand } = get();
        return Boolean(currentBrand?.subscription?.status === 'unpaid' && currentBrand?.subscription.cid)
    },

    getIsPaymentPastDue: () => {
        const { currentBrand } = get();
        // Check subscription_status field first (new centralized field), then fallback to subscription.status
        const subscriptionStatus = currentBrand?.subscription_status || currentBrand?.subscription?.status;
        return Boolean(subscriptionStatus === 'past_due' && currentBrand?.subscription?.cid);
    },

    getIsPaymentPaused: () => {
        const { currentBrand } = get();
        const subscriptionStatus = currentBrand?.subscription_status || currentBrand?.subscription?.status;
        return Boolean(subscriptionStatus === 'paused' && currentBrand?.subscription?.cid);
    },

    /** 
     * Fetches recent releases with retry logic 
     * Automatically retries up to 4 times with 5s delay between attempts
     */
    fetchReleases: async (brand?: CurrentBrand) => {
        const { currentBrand, isAnonymousBrand } = get();
        const { setInitialRecord } = useWebsiteBuilder.getState();

        if (!currentBrand || isAnonymousBrand) {
            return;
        }

        const { recentReleases } = get();

        const MAX_ATTEMPTS = 4;
        const RETRY_DELAY = 5000;
        let attempts = 0;

        const attemptFetch = async () => {
            set({ isLoadingReleases: true });

            try {
                const data = await getReleasesByBrandId_api({
                    brandId: brand?.id || currentBrand.id,
                    includePresaveReleases: false
                });

                if (data?.length) {
                    const releases = filterValidReleases(data);

                    const spotifyId = releases?.[0]?.content_metadata?.tracks?.[0]?.spotify_id || releases?.[0]?.content_metadata?.tracks?.[0]?.id || ''
                    const recordDetail = await getRecordDetails_api(spotifyId, 'single' as RecordSelectorType, currentBrand.id);
                    setInitialRecord(recordDetail);

                    set({
                        recentReleases: releases,
                        hasReleasesError: false,
                    });
                    return true;
                }

                return false;
            } catch (error) {
                console.error('Error fetching releases:', error);
                return false;
            } finally {
                set({ isLoadingReleases: false });
            }
        };

        const retryFetch = async () => {
            const success = await attemptFetch();
            if (success && recentReleases?.length) {
                return;
            }
            if (!success && attempts < MAX_ATTEMPTS) {
                attempts++;
                setTimeout(retryFetch, RETRY_DELAY);
            } else if (!success) {
                set({ hasReleasesError: true });
            }
        };

        await retryFetch();
    },

    /** 
     * Refreshes releases list by forcing a new fetch from the API and then fetching the latest releases
     * Useful when releases might have changed on the backend
     */
    refreshReleases: async () => {
        const { currentBrand, fetchReleases } = get();
        if (!currentBrand) {
            return;
        }

        set({ isRefreshingReleases: true });

        try {
            Sentry.setContext('refreshReleases', {
                brandId: currentBrand.id,
                component: 'RecordSelectorTab'
            });

            const refreshResult = await refreshBrandContent_api(currentBrand.id);
            if (!refreshResult?.success) {
                throw new Error('Failed to refresh brand content');
            }

            await fetchReleases();
        } catch (error) {
            console.error('Error refreshing releases:', error);
            Sentry.captureException(error);
            set({ hasReleasesError: true });
        } finally {
            set({ isRefreshingReleases: false });
        }
    },

    /** 
    * Fetches recent releases with retry logic 
    * Automatically retries up to 4 times with 5s delay between attempts
    */


    getBrandUsers: async (): Promise<BrandUsersApiResponse> => {
        try {
            const { currentBrand } = get();
            if (!currentBrand) {
                return { users: [], availableSeats: 0 };
            }

            const { users, availableSeats } = await getBrandUsers_api({ brandId: currentBrand.id });
            return { users, availableSeats };
        } catch (e) {
            console.error('Error fetching brand users:', e);
            return { users: [], availableSeats: 0 };
        }
    },

    deleteUserFromBrand: async (brandUser: BrandUser): Promise<BrandUsersApiResponse> => {
        const { currentBrand } = get();
        if (!currentBrand) {
            return { users: [], availableSeats: 0 };
        }

        await deleteUserFromBrand_api({ brandId: currentBrand.id, userId: brandUser.id! });
        const { users, availableSeats } = await getBrandUsers_api({ brandId: currentBrand.id });
        return { users, availableSeats };
    },

    addUserBrand: async (email: string): Promise<BrandUser | null> => {
        const { currentBrand } = get();
        if (!currentBrand) {
            return null;
        }

        const addedUser = await createUserBrand_api({ brandId: currentBrand.id, email });

        return addedUser;
    },

    /** Anonymous/Temporary Brand Methods */
    ensureBrandAvailable: async (): Promise<CurrentBrand> => {
        const { currentBrand, getOrCreateAnonymousSessionId } = get();

        // If we already have a brand (real or temporary), return it
        if (currentBrand) {
            return currentBrand;
        }

        // Use provided sessionId or get/create one from localStorage
        const finalSessionId = getOrCreateAnonymousSessionId();

        try {
            // Create or get temporary brand
            const response = await UnauthenticatedAxios.post('/temporary-brand', {
                sessionId: finalSessionId
            });

            if (!response.data?.brandId) {
                throw new Error('Invalid response: missing brandId');
            }

            // Create a temporary brand object with minimal required fields
            const tempBrand: CurrentBrand = {
                id: response.data.brandId,
                name: `Anonymous User`,
                slug: `temp-${finalSessionId}`,
                tier: PlanType.FREE,
                subscription: undefined,
                connections: {
                    isGrandfathered: false,

                } as any, // Empty connections object for anonymous users
                content_initialized: false,
                dashboard_initialized: false,
                image: null,
                website: {} as any,
                freeTrialOver: false,
                hideFansTab: false,
                conversionsStatus: undefined,
                affiliate_details: undefined,
                currency: { code: 'USD', symbol: '$' },
                showRecommendations: false,
                permissionLevel: 'owner' as any,
                organizationId: undefined,
                isGrandfathered: false, // Explicitly set for anonymous brand object
            };

            // Set anonymous brand state in store before calling setCurrentBrand
            set(state => {
                state.isAnonymousBrand = true;
                state.temporarySessionId = finalSessionId;
            });

            // Use setCurrentBrand to properly set up axios headers and other initialization
            console.log("🔄 Setting current brand in ensureBrandAvailable", tempBrand.id);
            get().setCurrentBrand(tempBrand, true); // ignoreOrganization = true for anonymous users

            // Save the session ID to localStorage for persistence
            saveAnonymousSessionIdToLocalStorage(finalSessionId);

            console.log(`✅ Created temporary brand ${tempBrand.id} for session ${finalSessionId}`);
            return tempBrand;

        } catch (error: any) {
            console.error('Failed to create temporary brand:', error);

            // Provide more specific error handling
            if (error.response?.status === 401) {
                throw new Error('Authentication failed while creating temporary brand');
            } else if (error.response?.status === 400) {
                throw new Error(`Invalid request: ${error.response.data?.message || 'Bad request'}`);
            } else if (error.response?.status >= 500) {
                throw new Error('Server error while creating temporary brand');
            } else if (error.code === 'NETWORK_ERROR' || !error.response) {
                throw new Error('Network error: Could not connect to server');
            }

            throw new Error(`Failed to create temporary brand: ${error.message}`);
        }
    },

    clearTemporaryBrand: () => {
        set(state => {
            if (state.isAnonymousBrand) {
                state.currentBrand = undefined;
                state.isAnonymousBrand = false;
                state.temporarySessionId = null;
                state.isPaidBrand = false;
                state.isTeamBrand = false;
                state.isProBrand = false;
                state.isLiteBrand = false;
                state.isFreeBrand = false;
                state.isStarterBrand = false;
                state.isGrowthBrand = false;
                state.isTeamV2Brand = false;
                state.isBrandAdmin = false;
            }
        });

        // Clean up localStorage when clearing temporary brand
        removeAnonymousSessionIdFromLocalStorage();

        console.log('✅ Cleared temporary brand from store and localStorage');
    },

    /** Get or create anonymous session ID from localStorage */
    getOrCreateAnonymousSessionId: () => {
        let sessionId = getAnonymousSessionIdFromLocalStorage();
        if (!sessionId) {
            sessionId = crypto.randomUUID();
            saveAnonymousSessionIdToLocalStorage(sessionId);
        }

        // Sync the temporarySessionId in the store
        set(state => {
            state.temporarySessionId = sessionId;
        });

        return sessionId;
    },

    getShouldShowNewUserUpgradePrompt: () => {
        const { isPaidBrand, isGrandfathered } = get();
        return !isPaidBrand && !isGrandfathered;
    }
}))
);

export default useBrand;